import { useMutation } from '@apollo/client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Collapse, Divider, Grid } from '@mui/material';

import { graphql, type DocumentType } from '@/gql';

import type { AssistantFormData } from '../../../form/AssistantForm';
import { AssistantSchema, AssistantForm } from '../../../form/AssistantForm';
import { ASSISTANT_DIALOG_QUERY } from '../../AssistantDialog';
import { MESSAGE_LIMIT } from '../AssistantConversation';

import { AssistantConversationLimitText } from './AssistantConversationLimitText';

const ADD_PROMPT_TO_CONVERSATION_MUTATION = graphql(`
  mutation AddPromptToConversation($params: AddPromptToConversationInput) {
    addPromptToConversation(params: $params) {
      ortooGptConversation {
        _id
      }
      ortooGptConversationItem {
        ...ortooGptConversationItemFields
        ...assistantAiResponseBubbleItemFields
      }
    }
  }
`);

export const ASSISTANT_CONVERSATION_ACTIONS_CONVERSATION_ITEMS_CONNECTION_FRAGMENT =
  graphql(`
    fragment assistantConversationActionsConversationItemsConnectionFields on OrtooGptConversationItemsConnection {
      edges {
        node {
          _id
        }
      }
    }
  `);

const ASSISTANT_CONVERSATION_ACTIONS_CONVERSATION_FRAGMENT = graphql(`
  fragment assistantConversationActionsConversationFields on OrtooGptConversation {
    _id
    itemsConnection {
      ...assistantConversationActionsConversationItemsConnectionFields
    }
  }
`);

interface Props {
  conversation:
    | DocumentType<typeof ASSISTANT_CONVERSATION_ACTIONS_CONVERSATION_FRAGMENT>
    | null
    | undefined;
}

export const AssistantConversationActions = ({ conversation }: Props) => {
  const { _id: conversationId, itemsConnection } = conversation ?? {};

  const currentMessageCount = itemsConnection?.edges?.length ?? 0;

  const overMessageLimit = currentMessageCount >= MESSAGE_LIMIT;

  const [
    addPromptToConversation,
    { loading: loadingMutation, error: errorMutation },
  ] = useMutation(ADD_PROMPT_TO_CONVERSATION_MUTATION, {
    update(cache, { data }) {
      const conversationId =
        data?.addPromptToConversation?.ortooGptConversation?._id;

      const conversationItemToAdd =
        data?.addPromptToConversation?.ortooGptConversationItem;

      if (!conversationItemToAdd || !conversationId) {
        return;
      }

      cache.updateQuery(
        {
          query: ASSISTANT_DIALOG_QUERY,
          variables: {
            ortooGptConversationId: conversationId,
            fetchConversation: true,
            first: 10,
            after: null,
            orderBy: 'CREATED',
          },
        },
        (queryData) => {
          if (!queryData?.ortooGptConversation?.itemsConnection)
            return queryData;

          const itemsConnection =
            queryData.ortooGptConversation.itemsConnection;

          const edges = [
            ...(itemsConnection.edges ?? []),
            {
              __typename: 'OrtooGptConversationItemsConnectionEdge' as const,
              node: {
                ...conversationItemToAdd,
              },
            },
          ];

          return {
            ...queryData,
            ortooGptConversation: {
              ...queryData.ortooGptConversation,
              __typename: 'OrtooGptConversation' as const,
              itemsConnection: {
                ...itemsConnection,
                __typename: 'OrtooGptConversationItemsConnection' as const,
                edges,
              },
            },
          };
        },
      );
    },
  });

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { isSubmitSuccessful },
    reset,
  } = useForm({
    defaultValues: {
      prompt: '',
      experimental: false,
      fileIds: [],
    },
    resolver: zodResolver(AssistantSchema),
  });

  // Reset the form after a successful submission
  useEffect(reset, [isSubmitSuccessful, reset]);

  const onSubmit = (data: AssistantFormData) => {
    if (!conversationId) {
      return;
    }

    addPromptToConversation({
      variables: {
        params: {
          conversation: conversationId,
          prompt: data.prompt,
          experimental: data.experimental,
          fileIds: data.fileIds ?? [],
        },
      },
    });
  };

  return (
    <Grid container width="100%" rowSpacing={1}>
      <Grid width="100%">
        <Divider />
      </Grid>
      <Grid container width="100%" rowGap={1}>
        <AssistantConversationLimitText
          currentMessageCount={currentMessageCount}
        />
        <Grid width="100%">
          <Collapse in={!overMessageLimit}>
            <AssistantForm
              control={control}
              getValues={getValues}
              setValue={setValue}
              loading={loadingMutation}
              disabled={overMessageLimit}
              errorMessage={errorMutation?.message}
              onSend={handleSubmit(onSubmit)}
            />
          </Collapse>
        </Grid>
      </Grid>
    </Grid>
  );
};
